import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  Text,
  Textarea,
} from '@chakra-ui/react';
import {Counter, IModalProps, Modal, Price} from 'components/shared';
import {useDesktop} from 'hooks';
import {ChevronDown, ChevronUp} from 'lucide-react';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import GTMService from 'services/ga4';
import {colors, modalCloseButtonStyles, pinkScrollBarStyles} from 'theme';
import {TranslationKeys} from 'translations';
import {ICartItem, IOptionSet, IOptionSets, IProduct, OptionSetTypeEnum} from 'types';
import {isProviderMcdonalds} from 'utils/helpers';
import logger from 'utils/logger';
import {CrossSellingSections} from 'utils/provider';
import {v4 as uuidv4} from 'uuid';

import {CrossSellingSets, OptionSets} from './components';

interface Props extends IModalProps {
  product: IProduct;
  onAdd?: (product: ICartItem, crossSelling: ICartItem[]) => void;
  onEdit?: (cartItem: ICartItem) => void;
  selectedCartItem?: ICartItem;
  t: (term: TranslationKeys) => string;
  isDisabledAddProduct?: boolean;
  crossSelingProducts?: CrossSellingSections[];
  providerSlug?: string;
  isFromEdit?: boolean;
}

const getDefaultOptions = (sets: IOptionSets[]): Record<number, Record<number, any>> => {
  const defaultOptions: Record<number, Record<number, any>> = {};

  sets.forEach((set) => {
    const selectedOptions = [];
    if (set.type === 1 && selectedOptions.length === 0) {
      const selectedOptionsObj: any = {};

      for (let i = 0; set.option_set.length > i; i++) {
        if (!set.option_set[i].is_selected) return;
        selectedOptionsObj[set.option_set[i].id] = {
          ...set.option_set[i],
          quantity: 1,
        };
        defaultOptions[set.id] = selectedOptionsObj;
        break;
      }
    }
  });

  return defaultOptions;
};

export const ProductDetailsModal: FC<Props> = ({
  isOpen,
  onClose,
  product,
  selectedCartItem,
  onAdd,
  onEdit,
  t,
  crossSelingProducts,
  isDisabledAddProduct,
  providerSlug,
  isFromEdit,
}) => {
  const {
    name,
    ingredients,
    measurement_display,
    price,
    discount,
    discount_price,
    option_sets,
    product_img_for_detail_thumbnail,
    details,
  } = product;
  const [quantity, setQuantity] = useState(selectedCartItem?.quantity ?? 1);
  const [optionsPrice, setOptionsPrice] = useState(0);
  const [instructions, setInstructions] = useState(selectedCartItem?.instructions ?? '');

  const isDesktop = useDesktop();
  const [selectedOptions, setSelectedOptions] = useState<Record<number, Record<number, any>>>(
    selectedCartItem?.selectedOptions ?? getDefaultOptions(option_sets),
  );
  const [selectedCrossSeling, setSelectedCrossSeling] = useState<IProduct[]>([]);
  const [isAddingDisabled, setIsAddingDisabled] = useState(isDisabledAddProduct);

  const isProviderMcDonalds = isProviderMcdonalds(providerSlug ?? '');
  const disableMentions = !!isProviderMcDonalds;
  const [requiredOptionsConfig, setRequiredOptionsConfig] = useState<IOptionSets[]>([]);
  const requiredSet: IOptionSets = requiredOptionsConfig[0] ?? {};
  const [isIngredientsExpanded, setIsIngredientsExpanded] = useState(false);
  const ingredientsRef = useRef<HTMLDivElement>(null);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);

  useEffect(() => {
    let tempOptionPrice = 0;
    Object.values(selectedOptions).map((set) =>
      Object.values(set).map((option) => {
        tempOptionPrice += option.price * option.quantity;
      }),
    );

    setOptionsPrice(tempOptionPrice);

    const allRequired = [];
    for (let index = 0; index < option_sets.length; index++) {
      let quantity = 0;
      const set = option_sets[index];

      if (selectedOptions.hasOwnProperty(set.id)) {
        Object.values(selectedOptions[set.id]).map((option) => {
          quantity += option.quantity;
        });
      }

      if (set.min_values > 0 && !!set.max_values) {
        if (quantity === set.max_values) continue;
        allRequired.push(set);
      }
    }
    setRequiredOptionsConfig(allRequired);
  }, [selectedOptions, option_sets]);

  useEffect(() => {
    setIsAddingDisabled(requiredOptionsConfig.length > 0);
  }, [requiredOptionsConfig]);

  useEffect(() => {
    if (ingredients && !measurement_display) {
      const tempElement = document.createElement('div');
      Object.assign(tempElement.style, {
        position: 'absolute',
        visibility: 'hidden',
        height: 'auto',
        width: ingredientsRef.current?.offsetWidth + 'px' || '300px',
        fontSize: window.innerWidth < 768 ? '14px' : '16px',
        fontWeight: '400',
        lineHeight: '1.5',
        fontFamily: 'inherit',
      });
      tempElement.innerHTML = ingredients;
      document.body.appendChild(tempElement);

      const lineHeight = parseFloat(window.getComputedStyle(tempElement).lineHeight);
      const lines = Math.floor(tempElement.offsetHeight / lineHeight);

      document.body.removeChild(tempElement);
      setShouldShowToggle(lines > 4);
    } else {
      setShouldShowToggle(false);
    }
  }, [ingredients, measurement_display]);

  const crossSellingItemsPrice = useMemo(
    () => selectedCrossSeling.reduce((sum, item) => sum + item.price, 0),
    [selectedCrossSeling],
  );

  const priceWithOptions = (optionsPrice + discount_price + crossSellingItemsPrice) * quantity;
  const productOptionsDiscount = useMemo(() => {
    let optionsWithDiscount = 0;
    let optionsPriceNoDiscount = 0;
    let hasDiscount = false;

    Object.values(selectedOptions).map((set) =>
      Object.values(set).map((option) => {
        if (option?.discount_price) hasDiscount = true;
        optionsWithDiscount += option?.discount_price ? option.discount_price : option.price * option.quantity;
        optionsPriceNoDiscount += option.price * option.quantity;
      }),
    );
    return {optionsWithDiscount, optionsPriceNoDiscount, hasDiscount};
  }, [selectedOptions]);

  const handleSubmit = () => {
    if (selectedCartItem) {
      const cartItem: ICartItem = {
        ...selectedCartItem,
        quantity,
        instructions,
        selectedOptions,
      };
      onEdit && onEdit(cartItem);
    } else {
      const cartItem: ICartItem = {
        id: uuidv4(),
        product,
        productId: product.id,
        price: product.price,
        quantity,
        instructions,
        selectedOptions,
      };
      const castedCross = [];
      for (let index = 0; index < selectedCrossSeling.length; index++) {
        const element = selectedCrossSeling[index];
        const crossCartItem: ICartItem = {
          id: uuidv4(),
          product: element,
          productId: element.id,
          price: element.price,
          quantity: 1,
          instructions: '',
          selectedOptions: {},
          added_from: 'product_recommended',
        };
        castedCross.push(crossCartItem);
      }
      onAdd && onAdd(cartItem, castedCross);
    }

    onClose();
  };

  const handleOptionChange = (set: IOptionSets, option_set: IOptionSet) => {
    const tempSelectedOptions = {...selectedOptions};
    const forceDeleteOption = option_set.quantity === 0;
    // const forceDeleteOption = option_set.quantity === 0 && option_set.max_qty !== 0;
    if (!selectedOptions.hasOwnProperty(set.id)) {
      tempSelectedOptions[set.id] = {};
    }

    if (forceDeleteOption) {
      delete tempSelectedOptions[set.id][option_set.id];
    } else {
      const selectedOptionsIds = Object.keys(selectedOptions[set.id] ?? {});

      const max_count = set.max_values !== 0 && option_set.quantity >= option_set.max_qty ? set.max_values : undefined;

      const isFullySelected = selectedOptionsIds.length === max_count;

      if (isFullySelected) {
        const optionsArr = Object.values(tempSelectedOptions[set.id]);

        delete tempSelectedOptions[set.id][optionsArr[optionsArr.length - 1].id];
      }

      if (set.type === OptionSetTypeEnum.ZERO) {
        tempSelectedOptions[set.id][option_set.id] = option_set; //TRUE
      } else {
        tempSelectedOptions[set.id] = {
          [option_set.id]: option_set,
        };
      }
    }

    setSelectedOptions(tempSelectedOptions);
  };

  const onChangeQuantityHandler = (value: number) => {
    if (value > 0) setQuantity(value);
  };

  const handleCrossSelling = (product: IProduct) => {
    const selectedCrossIds = selectedCrossSeling.map((e) => e.id);
    let newCross = [];
    if (selectedCrossIds.includes(product.id)) {
      newCross = selectedCrossSeling.filter((item) => item.id != product.id);
    } else {
      newCross = [...selectedCrossSeling, product];
    }
    setSelectedCrossSeling(newCross);
  };

  const selectedCross = useMemo(() => selectedCrossSeling.map((e) => e.id), [selectedCrossSeling]);

  const scrollToSection = (sectionId: string) => {
    logger.logDebug('scrollToSection', sectionId);
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({behavior: 'smooth', block: 'center'});
  };
  const [alreadySend, setalreadySend] = useState(false);
  useEffect(() => {
    if (!providerSlug) return;
    if (alreadySend) return;
    GTMService.viewItem(product, providerSlug);
    setalreadySend(true);
  }, [providerSlug, product, alreadySend]);

  // const isMcdAndRequired = requiredOptionsConfig.length > 0;

  const isIosMobile = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  const scrollLock = (() => {
    let scrollY = 0;
    let locked = false;

    return {
      lock() {
        if (locked) return;
        locked = true;

        scrollY = window.scrollY;
        Object.assign(document.body.style, {
          position: 'fixed',
          top: `-${scrollY}px`,
          left: '0',
          right: '0',
          overflow: 'hidden',
          width: '100%',
        });
      },
      unlock() {
        if (!locked) return;
        locked = false;

        Object.assign(document.body.style, {
          position: '',
          top: '',
          left: '',
          right: '',
          overflow: '',
          width: '',
        });
        window.scrollTo(0, scrollY);
      },
    };
  })();

  useEffect(() => {
    if (isOpen && !isDesktop) {
      scrollLock.lock();
      return () => scrollLock.unlock();
    }
  }, [isOpen, isDesktop, scrollLock]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} blockScrollOnMount={!isDesktop ? false : undefined}>
      <ModalContent
        maxW={['90%', '700px']}
        // iOS Safari specific height fixes
        maxH={['calc(88vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))', 'calc(100dvh - 10vh)']}
        my={['env(safe-area-inset-top)', '5vh']}
        top="1"
        mx={['10px', 0]}
        overflow="hidden"
      >
        <ModalCloseButton sx={modalCloseButtonStyles} />
        <ModalBody p={0} overflowY="scroll" sx={pinkScrollBarStyles}>
          <Image src={product_img_for_detail_thumbnail} fallbackSrc="/food_pattern.webp" alt="Product image" w="full" />

          <Box p={['15px', '30px']} pt="10px">
            <Box mb={['15px', '30px']}>
              {product_img_for_detail_thumbnail && (
                <Text fontWeight={400} color="#999999">
                  {`*${t('PRESENTATION_IMAGE_WARNING')}`}
                </Text>
              )}
              <Heading fontSize={['16px', '24px']} fontWeight={700} m="15px 0">
                {name}
              </Heading>
              {product.is_weighted_product && (
                <Text
                  position="absolute"
                  fontSize={['12px', '16px']}
                  fontWeight="700"
                  top={['10px', '40px']}
                  left="-10px"
                  p="3px 40px"
                  bgColor={colors.orange}
                  color={colors.white}
                  borderRadius="4px"
                >
                  {t('WEIGHTED')}
                </Text>
              )}
              {details && (
                <Text fontSize={['14px', '16px']} color={colors.textGray} fontWeight={400} mb="10px">
                  {details}
                </Text>
              )}
              {measurement_display ? (
                <Text fontSize={['14px', '16px']} color={colors.textGray} fontWeight={500}>
                  {ingredients}
                  <Text fontSize={['14px', '16px']} fontWeight={400}>
                    {measurement_display}
                  </Text>
                </Text>
              ) : (
                <Box position="relative">
                  <Text
                    ref={ingredientsRef}
                    fontSize={['14px', '16px']}
                    fontWeight={400}
                    noOfLines={isIngredientsExpanded || isDesktop ? undefined : 3}
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {ingredients}
                  </Text>
                  {shouldShowToggle && !isDesktop && (
                    <Flex justifyContent="flex-end" mt={1}>
                      <Button
                        variant="unstyled"
                        size="sm"
                        color={colors.pink}
                        fontWeight={500}
                        fontSize={['12px', '14px']}
                        p={0}
                        height="auto"
                        minH="auto"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        onClick={() => setIsIngredientsExpanded(!isIngredientsExpanded)}
                      >
                        {isIngredientsExpanded ? t('showLess') : t('showMore')}
                        {isIngredientsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </Button>
                    </Flex>
                  )}
                </Box>
              )}
            </Box>
            {/* TODO: if needed in future need to refactor, now when adding required options it flips to end and its bad ux */}
            {/* {!isMcdAndRequired && providerSlug == 'mcdonalds' && crossSelingProducts ? (
              <CrossSellingSets
                crossSelingProducts={crossSelingProducts}
                selectedOptions={selectedCross}
                t={t}
                onChange={handleCrossSelling}
              />
            ) : null} */}
            <OptionSets sets={option_sets} onChange={handleOptionChange} selectedOptions={selectedOptions} t={t} />
            {crossSelingProducts && (
              <CrossSellingSets
                crossSelingProducts={crossSelingProducts}
                selectedOptions={selectedCross}
                t={t}
                onChange={handleCrossSelling}
              />
            )}

            {!disableMentions && (
              <Textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder={t('SPECIAL_MENTIONS')}
                fontSize={isIosMobile ? '16px' : undefined}
              />
            )}
          </Box>
        </ModalBody>
        <ModalFooter
          p={['20px', '30px']}
          boxShadow="footer"
          flexDirection={['column', 'row']}
          alignItems={['flex-end', 'center']}
          justifyContent="space-between"
          position="relative"
        >
          {!!requiredSet && (
            <Text
              position="absolute"
              color="palette.red"
              top="4px"
              fontSize={['12px', '14px']}
              right={['unset', '16px']}
              left={['16px', 'unset']}
            >
              {requiredSet.name}
            </Text>
          )}
          <Flex justifyContent="space-between" alignItems="center" mb={['10px', '0px']} position="relative">
            <Price
              //  Product options discount
              value={
                productOptionsDiscount.hasDiscount
                  ? (productOptionsDiscount.optionsWithDiscount + crossSellingItemsPrice) * quantity
                  : priceWithOptions
              }
              priceProps={{
                fontSize: isDesktop ? '24px' : '16px',
                fontWeight: isDesktop ? '600' : 'bold',
              }}
            />
            {!!discount && (
              <Box
                _after={{
                  content: "''",
                  w: '65px',
                  display: 'block',
                  position: 'absolute',
                  top: '50%',
                  right: '-4px',
                }}
                ml="15px"
              >
                <Price
                  value={price}
                  priceProps={{
                    fontSize: '18px',
                    fontWeight: '500',
                    textDecorationLine: 'line-through',
                  }}
                />
              </Box>
            )}
            {/* Product options discount */}
            {productOptionsDiscount.hasDiscount && (
              <Box
                _after={{
                  content: "''",
                  w: '65px',
                  display: 'block',
                  position: 'absolute',
                  top: '50%',
                  right: '-4px',
                }}
                ml="15px"
              >
                <Price
                  value={priceWithOptions}
                  priceProps={{
                    fontSize: '18px',
                    fontWeight: '500',
                    textDecorationLine: 'line-through',
                  }}
                />
              </Box>
            )}
          </Flex>
          <Flex alignItems="center" justifyContent="space-between" w={['100%', 'auto']}>
            <Counter defaultValue={quantity} onChange={onChangeQuantityHandler} />
            <Button
              variant="primary"
              w={isAddingDisabled ? 'auto' : ['50%', '180px']}
              ml={['0.3rem', '1rem']}
              opacity={isAddingDisabled ? 0.8 : 1}
              onClick={() => (isAddingDisabled ? scrollToSection(`${requiredSet.id}`) : handleSubmit())}
              // disabled={isAddingDisabled}
            >
              {isFromEdit ? t('SAVE') : t('ADD_TO_CART')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
